import { useState } from 'react';
import { i18n } from '../../../i18n';
import styled from 'styled-components';
import {
  Typography,
  InfoItem,
  BorderLine,
  Button,
  updatePasswordModal,
} from '../../../design-system';
import {
  DashboardHeader,
  DashboardFormCards,
  DashboardFormCard,
  DashboardFooter,
} from '../../dashboard';
import { useStore } from '../../../context';
import { showToRoles } from '../../../utils';
import { canTradeOptions, rolesLabel } from '../../../const';
import { backupService } from '../../../services';

export const AccountPage = styled.div`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;
`;

export const AccountHeader = () => {
  const [isBackupDownloading, setIsBackupDownloading] = useState(false);
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const handleBackupClick = async () => {
    setIsBackupDownloading(true);

    try {
      await backupService.download();
    } finally {
      setIsBackupDownloading(false);
    }
  };

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.account.title')}
      </Typography>
      {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_VIEWER'], user) && (
        <Button
          id={'download-backup-button'}
          disabled={isBackupDownloading}
          onClick={handleBackupClick}
        >
          {i18n.t('button.downloadBackup')}
        </Button>
      )}
    </DashboardHeader>
  );
};

export const AccountForm = ({ company }: any) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const handleUpdatePasswordClick = () => {
    updatePasswordModal.open();
  };

  return (
    <DashboardFormCards id={'account-view'}>
      <DashboardFormCard paperSize={'full-size'}>
        <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('common.generalInformation')}
        </Typography>
        <InfoItem label={i18n.t('label.firstName')} value={user.firstName} />
        <InfoItem label={i18n.t('label.lastName')} value={user.lastName} />
        <InfoItem label={i18n.t('label.email')} value={user.email} />
        <InfoItem label={i18n.t('label.role')} value={rolesLabel[user.type]} />
        <InfoItem
          label={i18n.t('label.canTrade')}
          value={canTradeOptions.find((option) => option.value === user.canTrade)?.label}
        />
        <BorderLine space={'dashboard-form-card'} />
        <DashboardFooter>
          <Button id={'update-password-button'} onClick={handleUpdatePasswordClick}>
            {i18n.t('button.updatePassword')}
          </Button>
        </DashboardFooter>
      </DashboardFormCard>
      {company && (
        <>
          <DashboardFormCard paperSize={'full-size'}>
            <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
              {i18n.t('pages.account.companyTitle')}
            </Typography>
            <InfoItem
              label={
                showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user)
                  ? i18n.t('label.yardName')
                  : i18n.t('label.companyLegalName')
              }
              value={company.companyLegalName}
            />
            <InfoItem label={i18n.t('label.employeeCount')} value={company.employeeCount} />
            <InfoItem label={i18n.t('label.code')} value={company.code} />
            <InfoItem
              label={i18n.t('label.contactName')}
              value={`${company.contactPersonFirstName} ${company.contactPersonLastName}`}
            />
            <InfoItem label={i18n.t('label.email')} value={company.email} />
            <InfoItem label={i18n.t('label.phoneNumber')} value={company.phoneNumber} />
          </DashboardFormCard>
          <DashboardFormCard paperSize={'full-size'}>
            <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
              {i18n.t('pages.account.addressInformation')}
            </Typography>
            <InfoItem label={i18n.t('label.country')} value={company.country} />
            <InfoItem label={i18n.t('label.city')} value={company.city} />
            <InfoItem label={i18n.t('label.streetAddress')} value={company.streetAddress} />
            <InfoItem label={i18n.t('label.zipCode')} value={company.zipCode} />
          </DashboardFormCard>
        </>
      )}
    </DashboardFormCards>
  );
};
